/**
 * useSgpFlowData — data layer for SgpFlowScreen.
 * Extracted verbatim from screens/SgpFlowScreen.jsx (god-file decomposition).
 * Owns all screen state, effects, handlers, and derived values; returns the
 * exact prop set consumed by SgpFlowScreenView. The return list is generated
 * from the SAME prop list as the view's signature, so the two cannot drift.
 */

import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { View, Platform, Animated, Easing } from 'react-native';
import { useNavigation } from '../context/NavigationContext';
import { useTicker } from '../context/TickerContext';
import { useBackgroundQuery } from '../context/BackgroundQueryContext';
import { useFeedback } from '../context/FeedbackContext';
import { db, auth } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';
import { collection, addDoc, serverTimestamp, doc, setDoc, getDocs, query, orderBy, limit, where } from 'firebase/firestore';
import { getSportsbookUrl } from '../data/logos';
import { hydrateResultsForRender } from '../services/searchPipeline';
import { buildBackgroundHandoff, takeBackgroundHandoff, resolveRequestedLine } from '../utils/backgroundHandoff';
import { APP_VERSION } from '../constants/defaultPreferences';
import { getCanonicalSortEv, cleanPerBookEv } from '../utils/canonicalEv';
import { useConsensusOptions } from '../utils/useConsensusOptions';
import { normBookName } from '../../utils/resultSorting';
import { useSiteConfig } from '../context/SiteConfigContext';
import { makeSortEvForBook, makePreferredBook } from '../utils/resultPipelineHelpers';
import { buildSgpBookOdds, normalizeDates, normalizeQuery, getMarketRelevanceToQuery, _extractNamesFromSelection, _isOptionPlayerProp } from '../utils/sgpFlowUtils';
import { useFilteredAndSortedOptions } from './useFilteredAndSortedOptions';
import { useSearchExecution } from './useSearchExecution';
import { scrollSuggestedLegsIntoView } from '../utils/scrollSuggestedLegs';
import LegBetCard from '../components/LegBetCard';
import UnifiedBetCard from '../components/UnifiedBetCard';
import Constants from 'expo-constants';
import useWatchedFixtures from './useWatchedFixtures';
import useSgpBetSlip from './useSgpBetSlip';
import useSgpResultFacets from './useSgpResultFacets';
import useSgpFilterState from './useSgpFilterState';
import { responsive } from '../utils/responsive';

const mockSgpData = {
    totalLegs: 3,
    legs: [
        {
            id: 1,
            title: "Kevin Durant Points",
            description: "Bet on the total points scored by single player",
            options: [
                {
                    id: 1,
                    title: "NBA: Kevin Durant to score 20+ points",
                    description: "Bet on the total points scored by single player",
                    amount: "+$100",
                    odds: 1.85
                },
                {
                    id: 2,
                    title: "NBA: Kevin Durant to score 25+ points",
                    description: "Bet on the total points scored by single player",
                    amount: "+$150",
                    odds: 2.20
                },
                {
                    id: 3,
                    title: "NBA: Kevin Durant to score 35+ points",
                    description: "Bet on the total points scored by single player",
                    amount: "+$50",
                    odds: 3.50
                }
            ]
        },
        {
            id: 2,
            title: "LeBron James Assists",
            description: "Bet on the total assists by single player",
            options: [
                {
                    id: 4,
                    title: "NBA: LeBron James to get 8+ assists",
                    description: "Bet on the total assists by single player",
                    amount: "+$120",
                    odds: 1.90
                },
                {
                    id: 5,
                    title: "NBA: LeBron James to get 10+ assists",
                    description: "Bet on the total assists by single player",
                    amount: "+$200",
                    odds: 2.50
                },
                {
                    id: 6,
                    title: "NBA: LeBron James to get 12+ assists",
                    description: "Bet on the total assists by single player",
                    amount: "+$80",
                    odds: 4.00
                }
            ]
        },
        {
            id: 3,
            title: "Lakers Team Total",
            description: "Bet on the team's total points scored",
            options: [
                {
                    id: 7,
                    title: "NBA: Lakers to score 110+ points",
                    description: "Bet on the team's total points scored",
                    amount: "+$90",
                    odds: 1.75
                },
                {
                    id: 8,
                    title: "NBA: Lakers to score 120+ points",
                    description: "Bet on the team's total points scored",
                    amount: "+$180",
                    odds: 2.10
                },
                {
                    id: 9,
                    title: "NBA: Lakers to score 130+ points",
                    description: "Bet on the team's total points scored",
                    amount: "+$60",
                    odds: 3.20
                }
            ]
        }
    ]
};

const MAX_LEGS = 3;

export function useSgpFlowData() {
    // Navigation context
    const { goBack, routePayload, navigateTo, isAdmin } = useNavigation();
    const { userProfile, updateUserProfile, user, loading: authLoading } = useAuth();
    const { newResultsUiEnabled, newUiShowEverywhere } = useSiteConfig();
    // New card UI — two-toggle logic:
    //   Toggle 1 (newResultsUiEnabled): master on/off. OFF = old UI always.
    //   Toggle 2 (newUiShowEverywhere): scope. OFF = new UI only on staging/dev.
    //                                           ON  = new UI for all users everywhere.
    const _isDevOrStaging = (() => {
        try {
            if (Platform.OS !== 'web') return false;
            const h = (typeof window !== 'undefined' && window.location?.hostname) || '';
            return h.includes('localhost') || h.includes('127.0.0.1') || h.includes('staging');
        } catch { return false; }
    })();
    const shouldUseNewResultsUI = newResultsUiEnabled && (newUiShowEverywhere || _isDevOrStaging);
    const { isTickerEnabled, totalHeaderHeight, fixtures: tickerFixtures, referenceTime: tickerReferenceTime } = useTicker();
    // v1.15.x P3.3 — user-chosen consensus exclusion policy, threaded into
    // every cleanPerBookEv / getCanonicalSortEv call so the results grid
    // sort + per-book pills honour the toggle/custom-book-picker.
    const consensusOptions = useConsensusOptions();
    
    // ⭐ HELPER: Find fixture logos from ticker data (fallback for when query results don't have logos)
    // This is especially useful for tennis/individual sports where player photos are in fixture data
    const getFixtureLogosFromTicker = useCallback((fixtureId, homeTeam, awayTeam) => {
        if (!tickerFixtures || tickerFixtures.length === 0) return { homeLogo: null, awayLogo: null };
        
        // First try to find by fixture_id
        if (fixtureId) {
            const fixture = tickerFixtures.find(f => f.fixture_id === fixtureId || f.id === fixtureId);
            if (fixture && (fixture.home_logo || fixture.away_logo)) {
                return { homeLogo: fixture.home_logo, awayLogo: fixture.away_logo };
            }
        }
        
        // Fallback: try to match by team/player names
        if (homeTeam || awayTeam) {
            const homeLower = (homeTeam || '').toLowerCase();
            const awayLower = (awayTeam || '').toLowerCase();
            const fixture = tickerFixtures.find(f => {
                const fHome = (f.home_team || '').toLowerCase();
                const fAway = (f.away_team || '').toLowerCase();
                // Check if any team names match (flexible matching for player names)
                return (homeLower && (fHome.includes(homeLower) || homeLower.includes(fHome))) ||
                       (awayLower && (fAway.includes(awayLower) || awayLower.includes(fAway))) ||
                       (homeLower && (fAway.includes(homeLower) || homeLower.includes(fAway))) ||
                       (awayLower && (fHome.includes(awayLower) || awayLower.includes(fHome)));
            });
            if (fixture && (fixture.home_logo || fixture.away_logo)) {
                return { homeLogo: fixture.home_logo, awayLogo: fixture.away_logo };
            }
        }
        
        return { homeLogo: null, awayLogo: null };
    }, [tickerFixtures]);
    const { sendToBackground, canAddBackgroundQuery, IS_MOBILE, registerForegroundQuery, completeForegroundQuery, clearForegroundQuery, queryHistory, getQueryById: getBackgroundQueryById } = useBackgroundQuery();
    // ⭐ N5: "not what I meant" — thumbs-down opens the feedback modal prefilled
    const { openFeedbackFor } = useFeedback();
    
    // Navigation state
    const [currentLeg, setCurrentLeg] = useState(0); // 0-based index
    const [selectedOptions, setSelectedOptions] = useState({}); // { legId: optionId }
    
    // Selection state for current leg
    const [selectedCard, setSelectedCard] = useState(null); // 0 = first card selected by default
    const [pendingQuery, setPendingQuery] = useState('');
    /** Last query that produced / scopes current results — avoids re-filtering/sorting thousands of rows on every search-bar keystroke. */
    const [resultsContextQuery, setResultsContextQuery] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const pendingQueryRef = useRef(''); // ⭐ Ref to track latest query (avoids stale closure in event handlers)
    const processedQueriesRef = useRef(new Map()); // ⭐ Track processed background queries with timestamps for debounce
    const [fetchedOptions, setFetchedOptions] = useState([]); // normalized LegBetCard options from bOPT AI
    const scrolledLegsForQueryRef = useRef('');
    const [myBooksFetchedOptions, setMyBooksFetchedOptions] = useState([]); // My Books-filtered set (pre-sorted by personal EV)
    // ISO timestamp echoed from the server when FREEZE_TIME is active. Used as the
    // "now" reference in client-side date filters so archived data isn't dropped.
    const [referenceTime, setReferenceTime] = useState(null);
    const {
        selectedLegs, setSelectedLegs,
        fetchingAiProbs, setFetchingAiProbs,
        betMode, setBetMode,
        builderStake, setBuilderStake,
        activeFixtureId, setActiveFixtureId,
        lastNotice, setLastNotice,
        showShareModal, setShowShareModal,
        shareUrl, setShareUrl,
        showBetSlipModal, setShowBetSlipModal,
        showSubmissionScreen, setShowSubmissionScreen,
        computeCap,
        handleAddFetchedLeg,
        fetchAiProbabilities,
        handleSaveBetSlip,
        handleBetSlipSubmission,
        handleSubmissionComplete,
        handleShareBetSlip,
        handleCopyShareUrl,
    } = useSgpBetSlip({ userProfile, pendingQuery, navigateTo });
    const [loading, setLoading] = useState(false);
    const [isInitializing, setIsInitializing] = useState(true); // Prevent flash of content before query starts
    // Safety timer: if isInitializing is still true after 5s, force it out to prevent stuck logo screen
    const _initSafetyRef = useRef(null);
    
    // Query limit tracking
    const [showQueryLimitModal, setShowQueryLimitModal] = useState(false);
    const [queryUsage, setQueryUsage] = useState({ queriesUsed: 0, queryLimit: 3, planType: 'FREE' });
    
    // Watched fixtures tracking
    const { watchedFixtures, fetchWatchedFixtures, handleWatchFixture, handleUnwatchFixture } = useWatchedFixtures();

    // ── v1.16.x DEV TOOL — "Copy all results" ────────────────────────────────
    // The per-card Copy button (v1.16.3) dumps one card at a time; this copies
    // the whole visible list in rank order so we can diagnose sort/pill drift,
    // contamination, or "out of order" complaints in one paste. Gated admin OR
    // __DEV__ (same gate as the card-level button). The ref is populated from
    // inside the cards-IIFE below because `allGroupedCards` is scoped there —
    // ref writes during render are safe since they're idempotent and derived
    // purely from render inputs.
    const resultsListForCopyRef = useRef({
        cards: [],
        grouped: false,
        sortBy: 'ev',
        myBooksMode: false,
        totalItems: 0,
        currentPage: 1,
        totalPages: 1,
    });
    const [copyResultsFlash, setCopyResultsFlash] = useState(false);
    const [debugPanelExpanded, setDebugPanelExpanded] = useState(false);
    const _isAdminTop = (userProfile?.userType === 'admin');
    const canShowDevResultsCopy = _isAdminTop || (typeof __DEV__ !== 'undefined' && __DEV__);
    const handleCopyAllResults = useCallback(async () => {
        const snap = resultsListForCopyRef.current || { cards: [] };
        const pad = (s, n) => String(s).padEnd(n);
        const fmtEv = (v) => {
            if (v == null || !Number.isFinite(v)) return '  null ';
            const sign = v >= 0 ? '+' : '';
            return `${sign}${v.toFixed(2)}%`;
        };
        const fmtBookCount = (bo) => Object.keys(bo || {}).length;
        // v1.16.5 — numeric-aware line value. Totals used to dump as
        // `line=under` because `lineValue` can be a direction string ("over"/
        // "under") for totals items. Prefer the selection_line / points
        // numeric field when lineValue isn't a clean number.
        const _lineNumeric = (line) => {
            const r = line?.raw || line || {};
            const tryVals = [line?.lineValue, r.selection_line, r.handicap, r.line, r.points];
            for (const v of tryVals) {
                if (v == null || v === '') continue;
                if (typeof v === 'number' && Number.isFinite(v)) return v;
                if (typeof v === 'string') {
                    const n = parseFloat(v);
                    if (Number.isFinite(n)) return n;
                }
            }
            // v1.16.7 — last-resort fallback: parse from selection string itself.
            // Totals items commonly have selection="Over 218.5" / "Under 209.5"
            // with no populated lineValue/handicap/points field; without this
            // the dump showed `line=?` for every Totals ladder entry (Bug F).
            const sel = r.selection || line?.selection || '';
            if (typeof sel === 'string') {
                const m = sel.match(/[-+]?\d+(?:\.\d+)?/);
                if (m) {
                    const n = parseFloat(m[0]);
                    if (Number.isFinite(n)) return n;
                }
            }
            return null;
        };
        const _lineTitle = (line) => {
            const r = line?.raw || line || {};
            const mkt = r.market || line?.title || '';
            const sel = r.selection || line?.selection || '';
            const pts = _lineNumeric(line);
            return `${mkt} · ${sel}${pts != null ? ' ' + pts : ''}`.trim();
        };
        const _fmtStart = (s) => {
            if (s == null) return '';
            if (typeof s === 'string' || typeof s === 'number') return String(s).slice(0, 16);
            if (typeof s === 'object') {
                if (typeof s.toDate === 'function') {
                    try { return s.toDate().toISOString().slice(0, 16); } catch { /* fallthrough */ }
                }
                if (s._seconds != null) {
                    try { return new Date(s._seconds * 1000).toISOString().slice(0, 16); } catch { /* fallthrough */ }
                }
                if (s.seconds != null) {
                    try { return new Date(s.seconds * 1000).toISOString().slice(0, 16); } catch { /* fallthrough */ }
                }
                try { return JSON.stringify(s).slice(0, 40); } catch { return '[obj]'; }
            }
            return String(s).slice(0, 16);
        };
        // v1.16.5 — LIVE cleanPerBookEv computation (the actual sort axis).
        // `live_mb`  = max cleanPerBookEv across MyBooks on this line (empty → null)
        // `live_all` = max cleanPerBookEv across ALL books on this line
        // The pill on the card also uses cleanPerBookEv internally, so these
        // numbers represent ground truth for both pill display and sort.
        // Divergence from the backend `stamp` (ev_percent_mybooks / ev_percent_clean)
        // localizes whether a bug is in the list sort or in the card's pill.
        //
        // Read myBooks from userProfile directly — `_myBooksList` is declared
        // ~225 lines below this handler so accessing it here would hit the TDZ
        // even though closure timing normally saves us (the deps array
        // evaluates at handler-declaration time).
        const _mbListLive = userProfile?.preferences?.myBooks || [];
        const _mbNorm = _mbListLive.length > 0
            ? new Set(_mbListLive.map(b => normBookName(b)))
            : null;
        const _focusBookFp = snap.focusBook
            ? String(snap.focusBook).toLowerCase().replace(/\s*\([^)]*\)\s*$/, '').replace(/[^a-z0-9]+/g, '')
            : null;
        // v1.20.10 — SGP EV helper that mirrors UnifiedBetCard.perBookEV exactly.
        //
        // cleanPerBookEv() applies a Leave-One-Out R-factor correction for SGPs
        // (see canonicalEv.js lines ~365-412). The LOO correction is statistically
        // valid for ranking books against each other, but it is NOT what the card
        // displays — UnifiedBetCard.perBookEV uses a simpler formula:
        //   fp_X = (ev_method_X / 100 + 1) / combined_dec
        //   ev_at_book_X = (fp_X × bookDec − 1) × 100
        //   result = min(ev1, ev2, ev3)
        // When DraftKings IS the best-priced book (combined_price = DK price), the
        // LOO correction can swing EV by ~3pp (e.g. -3.0% → -0.08%) because EV
        // is near-zero and tiny changes in R propagate to the EV with high gain.
        // This made the sort axis disagree with the on-screen pill.
        //
        // Using this helper in ALL sort paths (My Books, Focus-Book, EV) and in the
        // dump's _liveEv guarantees: sort axis ≡ pill display ≡ dump column.
        // Unified per-book EV that routes BOTH SGPs and straights through the LOO
        // cleanPerBookEv path (the same path the UnifiedBetCard rail, the BetCardV2
        // pill, and the useFilteredAndSortedOptions sort axis use), so dump ≡ pill ≡
        // sort ≡ rail. (v1.18 P0: SGPs previously routed through sgpEvAtBookPrice,
        // which skips leave-one-out — so SgpFlowScreen's sort/dump disagreed with the
        // LOO card rail and the BetCardV2 pill for any correlated SGP.)
        const _sortEvForBook = (raw, bookData, isSGPHint) => {
            if (!bookData) return null;
            const isSGPItem = isSGPHint || raw?.ev_method === 'individual_leg_devig';
            if (isSGPItem) {
                const ev = cleanPerBookEv(raw, bookData, true, consensusOptions);
                if (ev !== null) return ev;
                // SGP but missing method stamps — fall back to stored ev_percent
                if (typeof bookData === 'object' && typeof bookData.ev_percent === 'number') {
                    return bookData.ev_percent;
                }
                return null;
            }
            return cleanPerBookEv(raw, bookData, isSGPHint, consensusOptions);
        };
        const _liveEv = (line, { myBooksOnly, focusBookOnly }) => {
            if (!line) return null;
            const r = line?.raw || line || {};
            const isSGP = r.ev_method === 'individual_leg_devig';
            const bo = r.bookOdds || line?.bookOdds || {};
            // v1.20.9 — when a focus book is set, return that book's EV directly so
            // the dump's pill/sort columns mirror the on-screen pill (which now sorts
            // by focus-book EV regardless of My Books mode). Cards without the focused
            // book return null, mirroring the "sink to bottom" behavior of the sort.
            if (focusBookOnly && _focusBookFp) {
                if (Number.isFinite(r.ev_percent_query_book)) return r.ev_percent_query_book;
                for (const [bk, bd] of Object.entries(bo)) {
                    const fp = String(bk).toLowerCase().replace(/\s*\([^)]*\)\s*$/, '').replace(/[^a-z0-9]+/g, '');
                    if (fp !== _focusBookFp) continue;
                    const wc = _sortEvForBook(r, bd, isSGP);
                    if (Number.isFinite(wc)) return wc;
                    if (Number.isFinite(bd?.clean_ev_percent)) return bd.clean_ev_percent;
                }
                return null;
            }
            // For samebook devig, per-book consensus computation diverges from the
            // backend-stamped EV when per-book clean_ev_percent is not cached per book
            // entry. The n-way devig fair prob (~0.95 for "player gets a hit") combined
            // with any book's decimal produces phantom +200%+ EV. Trust the server's
            // top-level EV directly to prevent these from floating to the top of the list.
            const _evSrcLabel = r._ev_source || r.devig_methods?.ev_source;
            if (_evSrcLabel === 'samebook') {
                const serverEv = myBooksOnly
                    ? (r.ev_percent_mybooks ?? r.ev_percent_clean ?? r.ev_percent ?? null)
                    : (r.ev_percent_clean ?? r.ev_percent ?? null);
                return Number.isFinite(serverEv) ? serverEv : null;
            }
            let best = null;
            for (const [bk, bd] of Object.entries(bo)) {
                if (myBooksOnly && _mbNorm && !_mbNorm.has(normBookName(bk))) continue;
                const wc = _sortEvForBook(r, bd, isSGP);
                if (Number.isFinite(wc) && (best == null || wc > best)) best = wc;
            }
            // Fallback for player props that have no devig_methods (e.g. single-book
            // or DFS-only markets): use the backend-stamped EV directly.
            if (best == null) {
                const fb = r.ev_percent_clean ?? r.ev_percent ?? null;
                if (Number.isFinite(fb)) best = fb;
            }
            return best;
        };
        // v1.16.6 — dump's pillLine tracks lines[0] (the post-sort best-EV
        // line), matching what UnifiedBetCard's workingOption now picks on
        // generic queries. Previous versions used a main-line picker that
        // diverged from the actual pill once v1.16.6 reverted card behavior.
        const _pickPillFromLines = (lines) => {
            if (!Array.isArray(lines) || lines.length === 0) return null;
            return lines[0];
        };
        const isMbMode = !!snap.myBooksMode;
        const focusBook = snap.focusBook || null;
        const sortAxisLabel = focusBook
            ? `focus_book=${focusBook}`
            : (isMbMode ? 'live_mybooks' : 'live_all_books');
        const out = [];
        out.push(`Results dump · ${new Date().toISOString()} · ${APP_VERSION}`);
        out.push(`sortBy=${snap.sortBy} · myBooksMode=${isMbMode} · grouped=${snap.grouped} · sortAxis=${sortAxisLabel}`);
        out.push(`page=${snap.currentPage}/${snap.totalPages} · totalCards=${snap.totalItems} · myBooks=[${_mbListLive.join(', ')}]`);
        out.push(`Columns: stamp=backend ev_percent_mybooks/clean · pill/sort=_sortEvForBook (card-consistent, no LOO for SGPs) · card=EV badge will show for selected book`);
        out.push('─'.repeat(96));
        (snap.cards || []).forEach(([key, val], idx) => {
            const isGroup = Array.isArray(val?.lines);
            const lines = isGroup ? val.lines : [val];
            const pillLine = isGroup ? _pickPillFromLines(lines) : val;
            const sortLine = isGroup ? lines[0] : val;
            const r = pillLine?.raw || pillLine || {};
            const bo = pillLine?.bookOdds || r.bookOdds || {};
            const bookCount = fmtBookCount(bo);
            const pillStamp = r.ev_percent_mybooks ?? r.ev_percent_clean ?? r.ev_percent ?? r.ev ?? null;
            const sortR = sortLine?.raw || sortLine || {};
            const sortStamp = sortR.ev_percent_mybooks ?? sortR.ev_percent_clean ?? sortR.ev_percent ?? sortR.ev ?? null;
            const liveMbPill   = _liveEv(pillLine, { myBooksOnly: true });
            const liveAllPill  = _liveEv(pillLine, { myBooksOnly: false });
            const liveFocusPill = focusBook ? _liveEv(pillLine, { focusBookOnly: true }) : null;
            const liveMbSort   = isGroup ? _liveEv(sortLine, { myBooksOnly: true }) : liveMbPill;
            const liveAllSort  = isGroup ? _liveEv(sortLine, { myBooksOnly: false }) : liveAllPill;
            const liveFocusSort = focusBook
                ? (isGroup ? _liveEv(sortLine, { focusBookOnly: true }) : liveFocusPill)
                : null;
            // v1.20.10 — compute the EV the card badge will actually show.
            // This is _sortEvForBook for the card's selected book (preferredSportsbook,
            // or best mybooks book when in My Books mode). Comparing this to pill/sort
            // quickly reveals any remaining sort≡display mismatches.
            const _getCardDisplayEv = (line) => {
                const rr = line?.raw || line || {};
                const boo = rr.bookOdds || line?.bookOdds || {};
                // 1. Focus book or preferredSportsbook wins.
                if (focusBook) {
                    const fp2 = String(focusBook).toLowerCase().replace(/\s*\([^)]*\)\s*$/, '').replace(/[^a-z0-9]+/g, '');
                    for (const [bk, bd] of Object.entries(boo)) {
                        const kfp = String(bk).toLowerCase().replace(/\s*\([^)]*\)\s*$/, '').replace(/[^a-z0-9]+/g, '');
                        if (kfp === fp2) return _sortEvForBook(rr, bd);
                    }
                }
                // 2. Best mybooks book.
                if (_mbNorm) {
                    let bestEv = null;
                    for (const [bk, bd] of Object.entries(boo)) {
                        if (!_mbNorm.has(normBookName(bk))) continue;
                        const ev = _sortEvForBook(rr, bd);
                        if (Number.isFinite(ev) && (bestEv == null || ev > bestEv)) bestEv = ev;
                    }
                    if (bestEv != null) return bestEv;
                }
                // 3. Best price book (global).
                return _liveEv(line, { myBooksOnly: false });
            };
            const cardDisplayEv = _getCardDisplayEv(pillLine);
            const src = r._ev_source || r.devig_methods?.ev_source || 'n/a';
            const propSrc = r._prop_source ? `[${r._prop_source}]` : '';
            const isMain = r.is_main === true ? 'yes' : (r.is_main === false ? 'no ' : ' - ');
            const rawEv = r.raw_ev_percent != null ? r.raw_ev_percent : r.ev_percent;
            const quality = r.ev_quality || '-';
            const outlier = r.outlier_reason ? `⚠${r.outlier_reason}` : '';
            const fx = r.fixture_id || r.game_id || '';
            const teams = (r.away_team && r.home_team) ? `${r.away_team} @ ${r.home_team}` : '';
            const start = _fmtStart(r.start_date);
            const rank = pad(`#${idx + 1}`, 4);
            const pillLabel = focusBook
                ? fmtEv(liveFocusPill)
                : (isMbMode ? fmtEv(liveMbPill) : fmtEv(liveAllPill));
            const sortLabel = focusBook
                ? fmtEv(liveFocusSort)
                : (isMbMode ? fmtEv(liveMbSort) : fmtEv(liveAllSort));
            out.push(
                `${rank} pill=${pad(pillLabel, 8)} sort=${pad(sortLabel, 8)} card=${pad(fmtEv(cardDisplayEv), 8)} ` +
                `stamp=${pad(fmtEv(pillStamp), 8)} books=${pad(bookCount, 3)} ` +
                `src=${pad(src, 14)} main=${isMain} alts=${pad(isGroup ? lines.length : 1, 2)} ` +
                `q=${pad(quality, 12)} raw=${pad(fmtEv(rawEv), 9)} ${propSrc}${outlier}`
            );
            if (Math.abs((liveMbPill ?? 0) - (liveAllPill ?? 0)) > 0.01
                || Math.abs((liveMbSort ?? 0) - (liveAllSort ?? 0)) > 0.01
                || Math.abs((pillStamp ?? 0) - (sortStamp ?? 0)) > 0.01) {
                out.push(
                    `     detail: live_mb_pill=${fmtEv(liveMbPill)}  live_all_pill=${fmtEv(liveAllPill)}  ` +
                    `live_mb_sort=${fmtEv(liveMbSort)}  live_all_sort=${fmtEv(liveAllSort)}  ` +
                    `stamp_pill=${fmtEv(pillStamp)}  stamp_sort=${fmtEv(sortStamp)}`
                );
            }
            out.push(`     ${_lineTitle(pillLine)}`);
            if (teams || start || fx) {
                out.push(`     ${teams}${start ? ' · ' + start : ''}${fx ? ' · fx=' + fx : ''}`);
            }
            if (isGroup && lines.length > 1) {
                const ladder = lines.slice(0, 8).map((l, i) => {
                    const rr = l?.raw || l || {};
                    const lv = _lineNumeric(l);
                    const lb = Object.keys(l?.bookOdds || rr.bookOdds || {}).length;
                    const me = (l === pillLine) ? ' ← pill' : '';
                    const mf = rr.is_main === true ? ' MAIN' : '';
                    const lvStr = lv != null ? lv : '?';
                    return `        [${i}] line=${lvStr} books=${lb}${mf}${me}`;
                });
                out.push(ladder.join('\n'));
                if (lines.length > 8) out.push(`        … ${lines.length - 8} more`);
            }
            out.push('');
            void key;
        });
        const text = out.join('\n');
        try {
            if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(text);
            } else {
                console.log('[SgpFlowScreen] clipboard unavailable, dump:\n' + text);
            }
            setCopyResultsFlash(true);
            setTimeout(() => setCopyResultsFlash(false), 1200);
        } catch (e) {
            console.warn('[SgpFlowScreen] copy-all failed:', e?.message || e);
            console.log('[SgpFlowScreen] dump:\n' + text);
        }
    }, [userProfile, consensusOptions]);
    
    // ⭐ Preferred sportsbook for "best X bets" queries - preselects this book on cards
    const [preferredSportsbook, setPreferredSportsbook] = useState(null);
    
    // ⭐ Requested line for centering alt lines (e.g., "+14" from "pistons +14 turnovers")
    const [requestedLine, setRequestedLine] = useState(null);
    const [requestedDirection, setRequestedDirection] = useState(null); // 'over' or 'under'

    // 🔴 Live query mode — set when payload.isLive=true (LIVE ticker tab or "live" keyword)
    const [isLiveQuery, setIsLiveQuery] = useState(false);
    
    // Sync sortMethod UI toggle with user preference (but NOT sortBy - let backend control that)
    useEffect(() => {
        const userDefault = userProfile?.preferences?.defaultSortMethod;
        if (userDefault && (userDefault === 'ev' || userDefault === 'odds')) {
            setSortMethod(userDefault);
            // ⚠️ DO NOT set sortBy here - it should stay 'relevance' to trust backend order
            // sortBy is only changed when user explicitly clicks a sort option
        }
    }, [userProfile?.preferences?.defaultSortMethod]);
    
    const [progress, setProgress] = useState(0);
    const {
        currentPage, setCurrentPage, itemsPerPage,
        filterSportsbooks, setFilterSportsbooks,
        filterMarkets, setFilterMarkets,
        filterPlayers, setFilterPlayers,
        filterResultType, setFilterResultType,
        filterDeepLinksOnly, setFilterDeepLinksOnly,
        filterPositiveEvOnly, setFilterPositiveEvOnly,
        filterLineType, setFilterLineType,
        filterLegCount, setFilterLegCount,
        searchText, setSearchText,
        toggleSportsbookFilter,
        toggleMarketFilter,
        togglePlayerFilter,
    } = useSgpFilterState({ userProfile });
    // ⭐ Show All Math toggle - Initialize based on user preference
    const [showAllMathOverride, setShowAllMathOverride] = useState(null); // null = use preference, true/false = override
    const [progressText, setProgressText] = useState('');
    const [avgMs, setAvgMs] = useState(8000); // Start with realistic default
    const [hasStartedSearch, setHasStartedSearch] = useState(false); // Track if search has actually started
    const [fixturesFoundNoOdds, setFixturesFoundNoOdds] = useState(null); // Track when fixtures exist but have no odds
    const [searchStatus, setSearchStatus] = useState({ step: '', fixturesFound: 0, elapsedMs: 0 }); // Detailed search status
    const [sgpGenerationStats, setSgpGenerationStats] = useState(null); // Stats about SGP generation (combinations_tested, markets_used)
    const [responseMetadata, setResponseMetadata] = useState(null); // ⭐ Store API response metadata for query resolution display
    const progressIntervalRef = useRef(null); // Track progress interval to prevent duplicates
    const [errorMsg, setErrorMsg] = useState('');
    const [noResults, setNoResults] = useState(false);
    // ⭐ N2: clarify-or-abstain — backend declined to guess between candidate
    // teams; holds { question, term, options: [{ label, revisedPrompt, ... }] }.
    const [clarification, setClarification] = useState(null);
    // ⭐ N3: broaden/fallback label from the backend — { kind, original,
    // substituted, note } when results were substituted for the exact ask.
    const [autoBroadened, setAutoBroadened] = useState(null);
    const [didBroaden, setDidBroaden] = useState(false);
    const lastRawRef = useRef('');
    const abortRef = useRef(null);
    const metricsQueryFiredRef = useRef(false); // prevents double-fire under React Strict Mode
    
    // Book-specific override state
    const [bookOverride, setBookOverride] = useState(null); // null = use defaults, string = specific book
    const [showBookSelector, setShowBookSelector] = useState(false);
    
    // Saved requests state
    const [showSavedRequests, setShowSavedRequests] = useState(false);
    
    // Minimum books filter override
    const [disableMinBooksFilter, setDisableMinBooksFilter] = useState(false);
    // True when disableMinBooksFilter was auto-enabled because background query results were all below threshold
    const [_relaxMinBooksFromBg, setRelaxMinBooksFromBg] = useState(false);
    const [disableMyBooksPriority, setDisableMyBooksPriority] = useState(false);
    // Session-level override for the books panel view on all cards.
    // null = follow profile preference (defaultBookSort); 'mybooks' = My Books tab on all cards; 'ev' = All Books.
    const [booksViewOverride, setBooksViewOverride] = useState(null);
    
    // Share betslip state
    const mountedRef = useRef(false);
    const [searchedSportsbooks, setSearchedSportsbooks] = useState([]);
    const [detectedPromotion, setDetectedPromotion] = useState(null);
    const [queryFeedback, setQueryFeedback] = useState(null); // 'up' | 'down' | null
    const [currentQueryMetricId, setCurrentQueryMetricId] = useState(null);
    
    // Filter states
    // Headshot URLs keyed by player name — populated by the chip-headshot enrichment effect below.
    // Covers H2H results where player_name is null so the main enrichment pass misses them.
    const {
        availableSportsbooks,
        availableMarkets,
        availablePlayers,
        hasTeamResults,
        hasPropsResults,
        isSgpResults,
        availableLegCounts,
        playerChipHeadshots,
        setPlayerChipHeadshots,
    } = useSgpResultFacets({ fetchedOptions, setFetchedOptions, resultsContextQuery });
    const [showIntlSportsbooks, setShowIntlSportsbooks] = useState(false); // expand to Tier 3 (international)
    const [showDfsSportsbooks,  setShowDfsSportsbooks]  = useState(false); // expand to Tier 4 (DFS props)
    // Sort method: 'ev' for Best EV%, 'odds' for Best Odds (uses user preference as default)
    const defaultSort = userProfile?.preferences?.defaultSortMethod || 'ev';
    const [sortMethod, setSortMethod] = useState(defaultSort); // 'ev' | 'odds'
    // ⭐ 'relevance' = trust backend order (default for specific queries with line thresholds).
    //
    // v1.20.6 — Single master switch for My Books mode: `defaultBookSort`.
    //   defaultBookSort === 'mybooks'  → My Books mode (filter + sort by My Books EV)
    //   defaultBookSort === 'ev'       → All Books mode (sort by All Books EV)
    //
    // The legacy `myBooksPriority` preference was removed entirely. Code that
    // historically branched on it now reads `defaultBookSort === 'mybooks'` directly.
    // The 'mybooks' option in `defaultSortMethod` was also removed (Display tab
    // only carries sort dimensions; the book set lives on the Sportsbooks tab).
    const _myBooksList = userProfile?.preferences?.myBooks || [];
    const _storedDefaultSort = (() => {
        const s = userProfile?.preferences?.defaultSortMethod || 'ev';
        // 'mybooks' is no longer a valid outer sort; My Books EV ordering is handled
        // by the 'ev' case when _wantsMyBooksMode is active.
        return s === 'mybooks' ? 'ev' : s;
    })();
    const _storedBooksPanelSort = userProfile?.preferences?.defaultBookSort || 'ev';
    // booksViewOverride lets the user flip All Books / My Books via the results-page toggle
    // without changing their saved profile preference. null = follow profile.
    const _effectiveBooksSort = booksViewOverride ?? _storedBooksPanelSort;
    const _wantsMyBooksMode = _myBooksList.length > 0 && _effectiveBooksSort === 'mybooks';
    const _defaultSortBy = _storedDefaultSort;
    const [sortBy, setSortBy] = useState(_defaultSortBy); // 'relevance' | 'price_asc' | 'price_desc' | 'ev' | 'date' | 'market' | 'mybooks' | 'sportsbooks'
    const [backendSorted, setBackendSorted] = useState(false); // Track if backend pre-sorted results

    // ─── SGP EV helpers (v1.20.10) ───────────────────────────────────────────────
    //
    // cleanPerBookEv() in canonicalEv.js applies a Leave-One-Out R-factor
    // correction for SGPs. The LOO correction is statistically valid for ranking
    // books against each other but NOT what the card displays. UnifiedBetCard
    // .perBookEV uses a simpler formula:
    //   fp_X = (ev_method_X / 100 + 1) / combined_dec
    //   ev_at_book_X = (fp_X × bookDec − 1) × 100
    //   result = min(ev1, ev2, ev3)
    // Using cleanPerBookEv for the sort produced a different axis than the pill
    // (e.g. sort said DK = -0.08%, card showed DK = -3.0%), making cards appear
    // out of order. These helpers now route through the same LOO cleanPerBookEv
    // path as the card rail, so sort ≡ dump ≡ pill ≡ rail.
    // Routes BOTH SGPs and straights through the LOO cleanPerBookEv path (same path
    // as the UnifiedBetCard rail, BetCardV2 pill, and results sort axis), so sort ≡
    // dump ≡ pill ≡ rail. (v1.18 P0: SGPs previously routed through sgpEvAtBookPrice,
    // which skips leave-one-out — see the parallel note above.)
    const _sortEvForBook = makeSortEvForBook(consensusOptions);

    // Returns the key of the best book for a given bookOdds map (utils/resultPipelineHelpers):
    // My Books mode picks the highest-EV book within the user's list, otherwise the
    // highest price. Shared with the Background Queries counts (utils/resultCounts).
    const _getPreferredBook = makePreferredBook({
        wantMyBooks: _wantsMyBooksMode || sortBy === 'mybooks',
        myBooksList: _myBooksList,
        sortEvForBook: _sortEvForBook,
    });

    // Legacy alias used inside the sort useMemo (keeps _getMyBooksBest references working).
    const _getMyBooksBest = _getPreferredBook;

    // Returns the preferred outer-results sort for reset / query-complete paths.
    // If the user has a My Books list, default to 'mybooks'; otherwise use their
    // stored default sort method (falling back to the provided fallback, usually 'ev').
    // This prevents new-query resets from clobbering the My Books preference.
    const _getPreferredOuterSort = (fallback = 'ev') => {
        return _storedDefaultSort || fallback;
    };

    // ⭐ Sync sortBy when preferences load OR change asynchronously.
    //
    // v1.20.6 — Previously this used `_sortSyncedRef` which flipped to `true` on the
    // FIRST sync and never re-fired, meaning later preference changes (e.g. flipping
    // Books Panel Default Sort from "All Books" to "My Books" in Profile) did not
    // update the active `sortBy`. The intent of the guard was always "don't clobber
    // a manual sort dropdown click", not "ignore preference changes after boot."
    //
    // Now: `_userPickedSortRef` flips ONLY when the user clicks the in-screen sort
    // dropdown (see the dropdown handler that calls `setSortBy(sort.value)`). This
    // effect re-runs freely on every preference change and follows the user's stored
    // preference unless they have actively overridden it for the current session.
    const _userPickedSortRef = useRef(false);
    useEffect(() => {
        if (_userPickedSortRef.current) return; // user picked a sort manually this session — don't override
        if (!userProfile?.preferences) return; // wait for profile to hydrate
        // 'mybooks' is no longer a sort option — My Books EV ordering is handled by the
        // 'ev' case when _wantsMyBooksMode is active. Normalize any stored 'mybooks' value to 'ev'.
        const _rawTarget = userProfile.preferences.defaultSortMethod || 'ev';
        const target = _rawTarget === 'mybooks' ? 'ev' : _rawTarget;
        console.log('[SortSync] preferences updated: ' + JSON.stringify({
            defaultSortMethod: userProfile.preferences.defaultSortMethod,
            defaultBookSort: userProfile.preferences.defaultBookSort,
            myBooksCount: (userProfile.preferences.myBooks || []).length,
            myBooksList: userProfile.preferences.myBooks || [],
            minBooksStraights: userProfile.preferences.minBooksStraights,
            _wantsMyBooksMode,
            currentSortBy: sortBy,
            targetSortBy: target,
        }));
        setSortBy((prev) => (prev === target ? prev : target));
    }, [userProfile?.preferences, _wantsMyBooksMode]); // eslint-disable-line react-hooks/exhaustive-deps
    
    // Modal state
    
    // Animation values for leg number buttons (individual for each button)
    const legNumberGlowT = useRef(
        Array.from({ length: mockSgpData.totalLegs }, () => new Animated.Value(0))
    ).current;

    // Page animation values
    const pageOpacity = useRef(new Animated.Value(0)).current;
    const pageTranslateY = useRef(new Animated.Value(20)).current;

    // Get current leg data
    const currentLegData = mockSgpData.legs[currentLeg];
    const currentLegOptions = currentLegData?.options || [];

    // Navigation functions
    const goToNextLeg = () => {
        if (currentLeg < mockSgpData.totalLegs - 1) {
            // Save current selection
            if (currentLegOptions[selectedCard]) {
                setSelectedOptions(prev => ({
                    ...prev,
                    [currentLegData.id]: currentLegOptions[selectedCard].id
                }));
            }
            
            setCurrentLeg(prev => prev + 1);
            // selectedCard will be restored by useEffect
        }
    };

    const goToPreviousLeg = () => {
        if (currentLeg > 0) {
            setCurrentLeg(prev => prev - 1);
            // selectedCard will be restored by useEffect
        }
    };

    // Check if all legs have selected options
    const isAllLegsSelected = () => {
        // Check if all previous legs have selections
        const previousLegsSelected = mockSgpData.legs.slice(0, currentLeg).every(leg => selectedOptions[leg.id] !== undefined);
        
        // Check if current leg has a selection (either saved or currently selected)
        const currentLegSelected = selectedOptions[currentLegData?.id] !== undefined || selectedCard !== undefined;
        
        return previousLegsSelected && currentLegSelected;
    };

    // Handle finish action
    const handleFinish = () => {
        if (isAllLegsSelected()) {
            // Save current selection before finishing
            if (currentLegOptions[selectedCard]) {
                setSelectedOptions(prev => ({
                    ...prev,
                    [currentLegData.id]: currentLegOptions[selectedCard].id
                }));
            }
            
            // Force open the bet slip modal
            setShowBetSlipModal(true);
        }
    };

    // Update selected card when leg changes
    useEffect(() => {
        // Check if there's a previously selected option for this leg
        const currentLegId = currentLegData?.id;
        if (currentLegId && selectedOptions[currentLegId]) {
            // Find the index of the selected option in the current leg's options
            const selectedOptionId = selectedOptions[currentLegId];
            const optionIndex = currentLegOptions.findIndex(option => option.id === selectedOptionId);
            if (optionIndex !== -1) {
                setSelectedCard(optionIndex);
            } else {
                setSelectedCard(null);
            }
        } else {
            setSelectedCard(null);
        }
    }, [currentLeg, selectedOptions, currentLegData, currentLegOptions]);
    

    // Leg number button hover animations
    const onLegNumberIn = (index) => {
        Animated.timing(legNumberGlowT[index], { toValue: 1, duration: 1500, easing: Easing.bezier(0.22, 1, 0.36, 1), useNativeDriver: true }).start();
    };

    const onLegNumberOut = (index) => {
        Animated.timing(legNumberGlowT[index], { toValue: 0, duration: 1500, easing: Easing.bezier(0.22, 1, 0.36, 1), useNativeDriver: true }).start();
    };
    
    // Card selection handlers (legacy mock flow)
    const handleCardPress = (cardIndex) => {
        setSelectedCard(cardIndex);
        setSelectedOptions(prev => ({
            ...prev,
            [currentLegData.id]: currentLegOptions[cardIndex].id
        }));
    };

    // normalizeDates / normalizeQuery moved to ../utils/sgpFlowUtils (pure).

    // Check query limits and execute if allowed
    const handleExecute = async (query) => {
        if (!query || !query.trim()) return;

        // Belt-and-suspenders: show loading before the async limit check.
        setLoading(true);
        setHasStartedSearch(true);
        setNoResults(false);
        
        // Get functions base URL — use same emulator-detection logic as executeQuery
        const isLocalhost = Platform.OS === 'web' && 
            (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
        const useEmulator = isLocalhost && (
            (typeof process !== 'undefined' && process?.env?.USE_EMULATOR === 'true') ||
            (typeof window !== 'undefined' && window.location?.search?.includes('emulator=true'))
        );
        
        const base = useEmulator
            ? 'http://127.0.0.1:5001/bet-optimized-fe922/us-central1'
            : (
                (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
                || (Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
                || (Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL)
                || (Constants?.manifest?.extra?.FUNCTIONS_BASE_URL)
                || 'https://us-central1-bet-optimized-fe922.cloudfunctions.net'
            );
        
        try {
            // Skip the limit check if auth hasn't resolved yet — avoids false positives
            // where the user appears anonymous (uid=null) mid-load
            if (!authLoading) {
                const checkResponse = await fetch(`${base}/checkAndIncrementQuery`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        uid: user?.uid || null,
                        ipAddress: null, // Server will use req.ip
                        hasResults: false, // Don't increment yet, just check
                    })
                });
                
                if (checkResponse.ok) {
                    const checkData = await checkResponse.json();
                    setQueryUsage(checkData);
                    
                    if (!checkData.allowed && !isAdmin && checkData.queryLimit !== 'unlimited') {
                        // Show limit modal — also ensure we're out of initializing state
                        setIsInitializing(false);
                        setLoading(false);
                        setShowQueryLimitModal(true);
                        return;
                    }
                }
            }
            
            // Execute the query
            await executeQuery(query.trim());
            
        } catch (error) {
            console.error('[handleExecute] Error checking query limit:', error);
            // If limit check fails, still allow the query (fail open)
            await executeQuery(query.trim());
        }
    };
    
    // Track successful query after results are received
    const trackQuerySuccess = async (hasResults) => {
        const isLocalhost = Platform.OS === 'web' &&
            (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
        const useEmulator = isLocalhost && (
            (typeof process !== 'undefined' && process?.env?.USE_EMULATOR === 'true') ||
            (typeof window !== 'undefined' && window.location?.search?.includes('emulator=true'))
        );

        const base = useEmulator
            ? 'http://127.0.0.1:5001/bet-optimized-fe922/us-central1'
            : (
                (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
                || (Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
                || (Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL)
                || (Constants?.manifest?.extra?.FUNCTIONS_BASE_URL)
                || 'https://us-central1-bet-optimized-fe922.cloudfunctions.net'
            );
        
        try {
            // Only increment if query returned results
            const trackResponse = await fetch(`${base}/checkAndIncrementQuery`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    uid: user?.uid || null,
                    ipAddress: null,
                    hasResults: hasResults,
                })
            });
            
            if (trackResponse.ok) {
                const trackData = await trackResponse.json();
                setQueryUsage(trackData);
                console.log('[trackQuerySuccess] Updated usage:', trackData);
            }
        } catch (error) {
            console.error('[trackQuerySuccess] Error tracking query:', error);
        }
    };

    // Core executor for nlOdds query — extracted verbatim to
    // hooks/useSearchExecution (god-file decomposition). All state resets,
    // progress animation, pipeline invocation, and result application live
    // there; behavior is identical. The params object mirrors the closure
    // the inline version captured.
    const executeQuery = useSearchExecution({
        // refs
        progressIntervalRef, abortRef, lastRawRef, mountedRef,
        // query context
        routePayload, betMode, bookOverride, userProfile, queryHistory, isLiveQuery,
        avgMs, filterSportsbooks,
        // state setters — results
        setResultsContextQuery, setErrorMsg, setNoResults, setIsInitializing,
        setLoading, setHasStartedSearch, setFixturesFoundNoOdds,
        setSgpGenerationStats, setResponseMetadata, setFetchedOptions,
        setMyBooksFetchedOptions, setSelectedLegs, setActiveFixtureId,
        setLastNotice, setCurrentPage, setClarification, setAutoBroadened,
        // state setters — filters/sort
        setFilterSportsbooks, setFilterMarkets, setFilterPlayers,
        setPlayerChipHeadshots, setFilterLegCount, setFilterDeepLinksOnly,
        setSearchText, setShowIntlSportsbooks, setShowDfsSportsbooks,
        setSortMethod, setSortBy, setBooksViewOverride,
        setDisableMinBooksFilter, setDisableMyBooksPriority, setBackendSorted,
        setFilterResultType, setFilterLineType, setFilterPositiveEvOnly, setRelaxMinBooksFromBg,
        // state setters — progress/status
        setProgress, setProgressText, setSearchStatus,
        // state setters — misc
        setPreferredSportsbook, setSearchedSportsbooks, setIsLiveQuery,
        setReferenceTime, setRequestedLine, setRequestedDirection,
        setDidBroaden, setCurrentQueryMetricId, setQueryFeedback, setAvgMs,
        // helpers from component scope
        _getPreferredOuterSort, registerForegroundQuery,
        completeForegroundQuery, trackQuerySuccess,
    });

    // ─── Deep link helper ───────────────────────────────────────────────────────
    // Handles all deeplink types consistently:
    //   • http/https URL → open in new tab
    //   • object with desktop/web/ios/android keys → pick best URL for platform
    //   • custom scheme (e.g. draftkings://) → fall back to sportsbook web homepage
    //     because custom schemes only work inside native apps, not browsers
    const openDeepLink = (urlOrObj, bookName) => {
        let url = urlOrObj;
        if (url && typeof url === 'object') {
            // Prefer desktop/web URL for web platform
            url = url.desktop || url.web || url.ios || url.android;
        }
        if (!url || typeof url !== 'string') return;

        if (url.startsWith('http')) {
            window.open(url, '_blank', 'noopener,noreferrer');
        } else {
            // Custom scheme (e.g. draftkings://) — not usable from a browser.
            // Fall back to the sportsbook's web homepage so the click always does something.
            const webFallback = bookName ? getSportsbookUrl(bookName) : null;
            if (webFallback) {
                window.open(webFallback, '_blank', 'noopener,noreferrer');
            }
            // If no fallback is available, silently do nothing rather than navigating
            // the current tab away (which caused the "display then return to main" bug).
        }
    };

    // Cancel loading
    const cancelLoading = () => {
        try { abortRef.current?.abort(); } catch {}
        // goBack() unmounts before the loadingRef effect runs; without this the
        // unmount auto-convert would re-run the cancelled search in background.
        loadingRef.current = false;
        try { clearForegroundQuery?.(); } catch {}
        if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
        }
        setLoading(false);
        setFetchedOptions([]);
        setMyBooksFetchedOptions([]);
        setSelectedLegs([]);
        setActiveFixtureId(null);
        goBack();
    };

    // Handle user feedback on query results
    const handleQueryFeedback = async (feedbackType) => {
        // ⭐ N5: "not what I meant" affordance — a thumbs-down opens the feedback
        // modal prefilled with this query (query_accuracy category) so the user
        // can say what they actually meant. Those reports feed the corpus
        // flywheel (scripts/corpus-flywheel.js) as labeled misclassifications.
        if (feedbackType === 'down') {
            openFeedbackFor({
                queryId: currentQueryMetricId || `local_${Date.now()}`,
                prompt: pendingQuery || null,
                resultsCount: fetchedOptions?.length ?? null,
            });
        }
        if (!currentQueryMetricId) return;

        try {
            const { doc, updateDoc } = await import('firebase/firestore');
            const metricRef = doc(db, 'metrics_queries', currentQueryMetricId);
            await updateDoc(metricRef, {
                feedback: feedbackType,
                feedbackAt: serverTimestamp()
            });
            setQueryFeedback(feedbackType);
        } catch (e) {
            console.error('[handleQueryFeedback] Error:', e);
        }
    };

    // Save query for later
    const handleSaveQuery = async (query) => {
        if (!query) return;
        
        try {
            const u = auth?.currentUser;
            if (!u) {
                alert('Please sign in to save queries');
                return;
            }
            
            const savedQuery = {
                uid: u.uid,
                query: query,
                teamName: fixturesFoundNoOdds?.teamName || null,
                fixtureCount: fixturesFoundNoOdds?.fixtureCount || 0,
                createdAt: serverTimestamp(),
                lastChecked: serverTimestamp(),
                notifyWhenAvailable: true
            };
            
            await addDoc(collection(db, 'saved_queries'), savedQuery);
            alert('Query saved! We\'ll notify you when odds are available.');
        } catch (e) {
            console.error('[handleSaveQuery] Error:', e);
            alert('Failed to save query. Please try again.');
        }
    };



    // Filter and sort options in real-time
    // Freeze is global on this backend, not ticker-only. Typed search and
    // Background Query View used to skip tickerReferenceTime, so May snapshot
    // games were dropped as "started 4+ hours ago" on today's wall clock.
    const filterReferenceTime = referenceTime || tickerReferenceTime || null;
    const _filterHookArgs = {
        fetchedOptions, myBooksFetchedOptions, resultsContextQuery, responseMetadata,
        referenceTime: filterReferenceTime, didBroaden, preferredSportsbook,
        searchText, filterSportsbooks, filterMarkets, filterPlayers, filterResultType,
        filterDeepLinksOnly, filterPositiveEvOnly, filterLineType, filterLegCount,
        disableMinBooksFilter, disableMyBooksPriority,
        sortBy, _effectiveBooksSort, _myBooksList,
        userProfile, consensusOptions,
        _extractNamesFromSelection, _getMyBooksBest, _isOptionPlayerProp, _sortEvForBook,
        getMarketRelevanceToQuery, normalizeQuery,
    };
    // Run both book modes through the same chip/+EV/line filters so the
    // All Books / My Books badges match the cards on screen (not the raw pool).
    const filteredAllBooksOptions = useFilteredAndSortedOptions({
        ..._filterHookArgs,
        _wantsMyBooksMode: false,
    });
    const filteredMyBooksOptions = useFilteredAndSortedOptions({
        ..._filterHookArgs,
        _wantsMyBooksMode: true,
    });
    const filteredAndSortedOptions = _wantsMyBooksMode ? filteredMyBooksOptions : filteredAllBooksOptions;
    const _allBooksCount = filteredAllBooksOptions.length;
    const _myBooksCount = _myBooksList.length > 0 ? filteredMyBooksOptions.length : 0;

    // ⭐ Create market peers map for true devigged market average calculation
    // Groups all selections (both sides) for each market so we can calculate no-vig odds
    // MUST be at top-level to follow React Rules of Hooks
    const marketPeersMap = useMemo(() => {
        const map = {};
        filteredAndSortedOptions.forEach(opt => {
            // Create market key: fixture + market + line (for over/under)
            const fixtureId = opt?.fixture_id || opt?.raw?.fixture_id || opt?._fixtureId || '';
            const market = opt?.market || opt?.raw?.market || '';
            const points = opt?.points || opt?.raw?.points || '';
            const marketKey = `${fixtureId}_${market}_${points}`.toLowerCase();
            
            if (!map[marketKey]) {
                map[marketKey] = [];
            }
            map[marketKey].push(opt);
        });
        return map;
    }, [filteredAndSortedOptions]);

    // Helper to get market peers for an option
    const getMarketPeers = useCallback((opt) => {
        const fixtureId = opt?.fixture_id || opt?.raw?.fixture_id || opt?._fixtureId || '';
        const market = opt?.market || opt?.raw?.market || '';
        const points = opt?.points || opt?.raw?.points || '';
        const marketKey = `${fixtureId}_${market}_${points}`.toLowerCase();
        return marketPeersMap[marketKey] || [];
    }, [marketPeersMap]);



    // Clear all filters
    const clearAllFilters = () => {
        setFilterSportsbooks([]);
        setFilterMarkets([]);
        setFilterPlayers([]);
        setFilterLegCount(null);
        setFilterResultType('all'); // Reset result type filter
        setFilterDeepLinksOnly(false);
        setFilterPositiveEvOnly(false); // Was omitted — Clear filters left +EV on
        setFilterLineType('all'); // Reset line type filter
        setDisableMinBooksFilter(true); // Chip clears do not imply min-books off
        setSearchText('');
        setSortMethod('ev');
        setSortBy(_getPreferredOuterSort('ev')); // Respect My Books mode
        setDidBroaden(true); // Skip relevance wipe after an explicit clear
        setCurrentPage(1);
    };

    // Retry without broadening
    const retryFetch = () => {
        executeQuery(pendingQuery, { broaden: false });
    };

    // Ask to broaden (explicit user opt-in)
    const requestBroaden = () => {
        executeQuery(pendingQuery, { broaden: true });
    };

    // ⭐ N2: user tapped a clarification chip — re-fire with the revised prompt
    // (nickname replaced by the full team name, which resolves deterministically).
    const resolveClarification = (option) => {
        const revised = option?.revisedPrompt;
        if (!revised) return;
        setClarification(null);
        setPendingQuery(revised);
        executeQuery(revised, { broaden: false });
    };

    // ⭐ N3: "Undo" from the broadened-results banner — re-run asking the
    // backend for exact matches only (honest empty state over substitution).
    const undoAutoBroaden = () => {
        setAutoBroadened(null);
        executeQuery(pendingQuery, { broaden: false, noAutoBroaden: true });
    };


    // Handle saved request application
    const handleApplySavedRequest = (request) => {
        setPendingQuery(request.query);
        if (request.books) {
            setBookOverride(request.books);
        }
        executeQuery(request.query);
    };

    // Handle saving a new request
    const handleSaveRequest = async (savedRequests) => {
        try {
            // Update user profile with saved requests
            const userRef = doc(db, 'users', userProfile.uid);
            await setDoc(userRef, {
                preferences: {
                    ...userProfile.preferences,
                    savedRequests: savedRequests
                }
            }, { merge: true });
        } catch (error) {
            console.error('Error saving request:', error);
        }
    };


    // Exit animation function
    const exitAnimation = (callback) => {
        Animated.parallel([
            Animated.timing(pageOpacity, {
                toValue: 0,
                duration: 600,
                easing: Easing.in(Easing.cubic),
                useNativeDriver: true,
            }),
            Animated.timing(pageTranslateY, {
                toValue: 20,
                duration: 600,
                easing: Easing.in(Easing.cubic),
                useNativeDriver: true,
            }),
        ]).start(() => {
            if (callback) callback();
        });
    };

    // Page load animation
    useEffect(() => {
        Animated.parallel([
            Animated.timing(pageOpacity, {
                toValue: 1,
                duration: 1000,
                easing: Easing.inOut(Easing.cubic),
                useNativeDriver: true,
            }),
            Animated.timing(pageTranslateY, {
                toValue: 0,
                duration: 1000,
                easing: Easing.inOut(Easing.cubic),
                useNativeDriver: true,
            }),
        ]).start();
    }, []);

    // Fetch average query time from recent searches on mount.
    // useRef guard prevents double-fire: refs persist across Strict Mode's
    // unmount/remount cycle while local variables reset.
    useEffect(() => {
        if (metricsQueryFiredRef.current) return;
        if (!user || !isAdmin) return; // metrics_queries is admin-only; skip if not authenticated/admin
        metricsQueryFiredRef.current = true;
        const fetchAvgQueryTime = async () => {
            try {
                const metricsRef = collection(db, 'metrics_queries');
                const q = query(metricsRef, orderBy('createdAt', 'desc'), limit(20));
                const snapshot = await getDocs(q);
                
                if (!snapshot.empty) {
                    const durations = snapshot.docs
                        .map(doc => doc.data().durationMs)
                        .filter(d => d && d > 0);
                    
                    if (durations.length > 0) {
                        const avg = Math.round(durations.reduce((sum, d) => sum + d, 0) / durations.length);
                        console.log(`[SGP] Fetched average query time: ${avg}ms from ${durations.length} recent queries`);
                        setAvgMs(avg);
                    }
                }
            } catch (err) {
                console.error('[SGP] Error fetching average query time:', err);
            }
        };
        
        fetchAvgQueryTime();
    }, [user, isAdmin]);

    // Handle photo upload progress
    useEffect(() => {
        const isPhotoUpload = routePayload?.photoUploadInProgress;
        if (!isPhotoUpload || Platform.OS !== 'web') return;
        
        setIsInitializing(false); // Done initializing, now showing loading state
        setLoading(true);
        setProgress(0);
        setProgressText('Preparing...');
        
        // Poll for progress updates from window.photoUploadProgress
        const pollInterval = setInterval(() => {
            try {
                const photoProgress = window.photoUploadProgress;
                if (photoProgress) {
                    setProgress(photoProgress.progress || 0);
                    setProgressText(photoProgress.message || 'Processing...');
                    
                    if (photoProgress.step === 'error') {
                        setErrorMsg(photoProgress.message);
                        setLoading(false);
                        clearInterval(pollInterval);
                        delete window.photoUploadProgress;
                        setTimeout(() => goBack(), 3000);
                    }
                }
                
                // Check if query is ready to execute
                const triggerData = window.triggerPhotoQuery;
                if (triggerData && triggerData.query) {
                    const { query, promotion } = triggerData;
                    setPendingQuery(query);
                    setDetectedPromotion(promotion);
                    delete window.triggerPhotoQuery;
                    delete window.photoUploadProgress;
                    clearInterval(pollInterval);
                    // Small delay to show "Found: ..." message
                    setTimeout(() => executeQuery(query), 800);
                }
            } catch (e) {
                console.error('[photoUpload] poll error:', e);
            }
        }, 200);
        
        // Cleanup on unmount
        return () => {
            clearInterval(pollInterval);
            try {
                delete window.photoUploadProgress;
                delete window.triggerPhotoQuery;
            } catch {}
        };
    }, [routePayload?.photoUploadInProgress]);

    // Apply a finished background query (utils/backgroundHandoff) to results
    // state. Shared by the route-payload effect and the same-page View event so
    // both entry points behave identically. Returns false when there is nothing
    // to apply; a completed query with 0 results shows the empty state.
    const applyBackgroundHandoff = (handoff, prompt) => {
        if (!handoff) return false;
        const rawResults = handoff.results || [];
        // buildSgpBookOdds returns null for non-renderable cached SGPs.
        const renderable = hydrateResultsForRender(rawResults).filter(Boolean);
        if (renderable.length < rawResults.length) {
            console.log(`[SgpFlow] 🫥 Dropped ${rawResults.length - renderable.length} unrenderable cached SGPs from background payload`);
        }
        if (handoff.preferredSportsbook) {
            setPreferredSportsbook(handoff.preferredSportsbook);
            setFilterSportsbooks([handoff.preferredSportsbook]);
        } else if (handoff.bookUnavailable) {
            // Same as the direct search: the book priced none of these, so no book filter.
            setPreferredSportsbook(null);
            setFilterSportsbooks([]);
        }
        // Start from the same clean slate as a new search: the relevance and date
        // filters read resultsContextQuery, and per-search filters from the previous
        // query (result type, lines, players, +EV, books view) must not hide these results.
        setResultsContextQuery(prompt || '');
        setDidBroaden(false);
        setFilterResultType('all');
        setFilterLineType('all');
        setFilterPlayers([]);
        setFilterMarkets([]);
        setFilterPositiveEvOnly(!!userProfile?.preferences?.filterPositiveEvOnly);
        setBooksViewOverride(null);
        if (!handoff.relaxMinBooks) setDisableMinBooksFilter(false);
        const { line, direction } = resolveRequestedLine(handoff.lineMetadata, prompt);
        setRequestedLine(line);
        setRequestedDirection(direction);
        // Query classification drives team-aware sorting (e.g. Devils before Panthers).
        setResponseMetadata(handoff.queryClassification || handoff.bookUnavailable ? {
            _queryClassification: handoff.queryClassification || null,
            _bookUnavailable: handoff.bookUnavailable || null,
            _message: handoff.bookUnavailable ? handoff.message : null,
        } : null);
        setReferenceTime(handoff.referenceTime || null);
        setFetchedOptions(renderable);
        setMyBooksFetchedOptions(handoff.myBooksResults?.length ? hydrateResultsForRender(handoff.myBooksResults) : []);
        if (handoff.relaxMinBooks) {
            setDisableMinBooksFilter(true);
            setRelaxMinBooksFromBg(true);
        } else {
            setRelaxMinBooksFromBg(false);
        }
        setErrorMsg('');
        setClarification(null);
        setNoResults(renderable.length === 0);
        setSortBy(_getPreferredOuterSort('ev'));
        setBackendSorted(true);
        setProgress(100);
        setHasStartedSearch(true);
        setIsInitializing(false);
        setLoading(false);
        return true;
    };

    // Fetch legs for incoming query payload
    useEffect(() => {
        const payloadQuery = routePayload?.query || '';
        const payloadPromotion = routePayload?.promotion || null;
        const isPhotoUpload = routePayload?.photoUploadInProgress;
        const isFromBackground = routePayload?.fromBackground === true;
        
        // Skip if this is a photo upload (handled by separate effect above)
        if (isPhotoUpload) return;
        
        let q = payloadQuery;
        if (!q && Platform.OS === 'web') {
            try { q = window.sessionStorage.getItem('sgp:lastQuery') || ''; } catch {}
        }
        
        // No query to run - stop initializing and show empty state
        if (!q) {
            setIsInitializing(false);
            return;
        }
        
        setPendingQuery(q);
        setDetectedPromotion(payloadPromotion);

        // Apply sportsbook filter(s) from the optimizer — handles 2+ selected books
        const payloadSportsbooks = routePayload?.filters?.sportsbooks;
        if (payloadSportsbooks?.length > 0) {
            setFilterSportsbooks(payloadSportsbooks);
        }
        
        // Background "View": render the finished results. A completed query is
        // never re-run (see utils/backgroundHandoff.js).
        if (isFromBackground) {
            const handoff = takeBackgroundHandoff(routePayload?.queryId)
                || buildBackgroundHandoff(getBackgroundQueryById?.(routePayload?.queryId));
            if (applyBackgroundHandoff(handoff, q)) return;
        }
        
        // No cached results — enter loading immediately so we never flash "No odds found"
        // while handleExecute awaits the query-limit check (that gap used to leave
        // loading=false with stale hasStartedSearch from a prior visit).
        setFetchedOptions([]);
        setMyBooksFetchedOptions([]);
        setNoResults(false);
        setErrorMsg('');
        setClarification(null);
        setLoading(true);
        setHasStartedSearch(true);
        setProgress(0);
        setProgressText('Preparing search…');
        setSearchStatus({ step: 'Starting search', fixturesFound: 0, resultsFound: null, elapsedMs: 0 });
        setIsInitializing(false);
        handleExecute(q);
    }, [routePayload]);
    
    // Listen for background query navigation events (when clicking View on another query while on results page)
    useEffect(() => {
        if (Platform.OS !== 'web') return;
        
        const handleBackgroundNav = (event) => {
            const { queryId, prompt } = event.detail || {};
            
            if (!prompt) return;
            
            // Debounce rapid clicks on the same query (300ms cooldown)
            const lastProcessedTime = processedQueriesRef.current.get(queryId) || 0;
            if (queryId && Date.now() - lastProcessedTime < 300) {
                return;
            }
            if (queryId) {
                processedQueriesRef.current.set(queryId, Date.now());
            }
            
            // ⭐ SCROLL TO TOP AND RESET PAGE when viewing a new background query
            setCurrentPage(1);
            try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch {}
            
            // ⭐ Always reset these states
            setSelectedLegs([]);
            setPendingQuery(prompt);
            setPreferredSportsbook(null); // ⭐ Reset sportsbook preference for new query
            
            // ⭐ FIX: Reset all filters when switching to a new query
            setFilterSportsbooks([]);
            setFilterMarkets([]);
            setFilterLegCount(null);
            setFilterDeepLinksOnly(false);
            setFilterLineType('all');
            setSortBy(_getPreferredOuterSort('ev'));
            setBackendSorted(false);
            setSearchText('');
            
            // Finished results FIRST: a completed query is never re-run.
            const handoff = takeBackgroundHandoff(queryId)
                || buildBackgroundHandoff(getBackgroundQueryById?.(queryId));
            const loadedResults = applyBackgroundHandoff(handoff, prompt);
                
            // ⭐ FIX: Only execute query if we didn't successfully load pre-fetched results
            if (!loadedResults) {
                // Set loading states only when we're actually going to execute
                setLoading(true);
                setProgress(0);
                setHasStartedSearch(true);
                setIsInitializing(false);
                setFetchedOptions([]); setMyBooksFetchedOptions([]); // Clear state only when executing fresh
                // Small delay to ensure state is cleared before executing
                setTimeout(() => {
                    handleExecute(prompt);
                }, 50);
            }
        };
        
        window.addEventListener('backgroundQueryNavigation', handleBackgroundNav);
        return () => {
            window.removeEventListener('backgroundQueryNavigation', handleBackgroundNav);
        };
    }, [handleExecute]);
    
    // Load initial query usage on mount
    useEffect(() => {
        const loadQueryUsage = async () => {
            const isLocalhost = Platform.OS === 'web' && 
                (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
            const useEmulator = isLocalhost && (
                (typeof process !== 'undefined' && process?.env?.USE_EMULATOR === 'true') ||
                (typeof window !== 'undefined' && window.location?.search?.includes('emulator=true'))
            );
            
            const base = useEmulator
                ? 'http://127.0.0.1:5001/bet-optimized-fe922/us-central1'
                : (
                    (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
                    || (Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
                    || (Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL)
                    || (Constants?.manifest?.extra?.FUNCTIONS_BASE_URL)
                    || 'https://us-central1-bet-optimized-fe922.cloudfunctions.net'
                );
            
            try {
                const response = await fetch(`${base}/getQueryUsage`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        uid: user?.uid || null,
                        ipAddress: null,
                    })
                });
                
                if (response.ok) {
                    const data = await response.json();
                    setQueryUsage(data);
                    console.log('[SGP] Loaded query usage:', data);
                }
            } catch (error) {
                console.error('[SGP] Error loading query usage:', error);
            }
        };
        
        loadQueryUsage();
    }, [user?.uid]);

    // Fetch watched fixtures on mount and when user changes
    useEffect(() => {
        if (user?.uid) {
            fetchWatchedFixtures();
        }
    }, [user?.uid]);

    // Register exit animation with navigation context
    useEffect(() => {
        if (Platform.OS === 'web') {
            window.sgpFlowExitAnimation = exitAnimation;
        }

        return () => {
            if (Platform.OS === 'web') {
                delete window.sgpFlowExitAnimation;
            }
        };
    }, []);

    // Auto-convert to background when navigating away while loading (desktop only)
    // This stores the current loading state to be checked on unmount
    const loadingRef = useRef(loading);
    // ⭐ pendingQueryRef already declared above - reuse it
    const alreadySentToBackgroundRef = useRef(false); // Prevent duplicate sends
    
    useEffect(() => {
        loadingRef.current = loading;
        pendingQueryRef.current = pendingQuery;
    }, [loading, pendingQuery]);

    // The unmount cleanup below is created once at mount; read everything it
    // needs through this ref so it sees the current limit, payload and mode.
    const unmountConvertRef = useRef({});
    unmountConvertRef.current = { sendToBackground, canAddBackgroundQuery, routePayload, betMode };
    
    useEffect(() => {
        // Only on desktop, auto-convert to background when navigating away while loading
        if (IS_MOBILE) return;
        
        return () => {
            const { sendToBackground, canAddBackgroundQuery, routePayload, betMode } = unmountConvertRef.current;
            // Check if we're still loading when unmounting AND haven't already sent to background
            if (loadingRef.current && pendingQueryRef.current && canAddBackgroundQuery && !alreadySentToBackgroundRef.current) {
                // Don't hardcode nba/basketball defaults — let classifyQuery on the
                // backend detect league/sport from the prompt. Hardcoding caused
                // wrong-league contamination for NHL/NFL/etc. searches that left
                // SgpFlow mid-execution.
                sendToBackground(pendingQueryRef.current, {
                    ...(routePayload?.league ? { league: routePayload.league } : {}),
                    ...(routePayload?.sport ? { sport: routePayload.sport } : {}),
                    prefer: betMode,
                    fixtureId: routePayload?.fixtureId || routePayload?.fixture_id || null,
                });
            }
        };
    }, []); // Empty deps - only run cleanup on unmount

    // Safety timeout: if still initializing after 5 seconds, force out of stuck state
    useEffect(() => {
        if (!isInitializing) {
            if (_initSafetyRef.current) {
                clearTimeout(_initSafetyRef.current);
                _initSafetyRef.current = null;
            }
            return;
        }
        _initSafetyRef.current = setTimeout(() => {
            console.warn('[SgpFlow] Safety timeout: isInitializing stuck for 5s, forcing out');
            setIsInitializing(false);
        }, 5000);
        return () => {
            if (_initSafetyRef.current) {
                clearTimeout(_initSafetyRef.current);
                _initSafetyRef.current = null;
            }
        };
    }, [isInitializing]);

    // After results land, jump to Suggested Legs so cards aren't buried under the filter chip cloud.
    useEffect(() => {
        if (Platform.OS !== 'web') return;
        if (loading || isInitializing) return;
        if (!fetchedOptions.length) return;
        const key = resultsContextQuery || pendingQuery || 'results';
        if (scrolledLegsForQueryRef.current === key) return;
        scrolledLegsForQueryRef.current = key;
        const t = setTimeout(() => {
            try { scrollSuggestedLegsIntoView(); } catch {}
        }, 80);
        return () => clearTimeout(t);
    }, [loading, isInitializing, fetchedOptions.length, resultsContextQuery, pendingQuery]);

    // Initializing state - show minimal loading to prevent flash of results
  return { IS_MOBILE, MAX_LEGS, cardReferenceTime: filterReferenceTime, _allBooksCount, _effectiveBooksSort, _getMyBooksBest, _isAdminTop, _myBooksCount, _myBooksList, _relaxMinBooksFromBg, _sortEvForBook, _userPickedSortRef, _wantsMyBooksMode, alreadySentToBackgroundRef, availableLegCounts, availableMarkets, availablePlayers, availableSportsbooks, avgMs, autoBroadened, betMode, bookOverride, builderStake, canAddBackgroundQuery, canShowDevResultsCopy, cancelLoading, clarification, setClarification, clearAllFilters, computeCap, consensusOptions, copyResultsFlash, currentPage, debugPanelExpanded, detectedPromotion, didBroaden, disableMinBooksFilter, disableMyBooksPriority, errorMsg, fetchedOptions, filterDeepLinksOnly, filterLegCount, filterLineType, filterMarkets, filterPlayers, filterPositiveEvOnly, filterResultType, filterSportsbooks, filteredAndSortedOptions, fixturesFoundNoOdds, getFixtureLogosFromTicker, getMarketPeers, goBack, handleAddFetchedLeg, handleApplySavedRequest, handleBetSlipSubmission, handleCopyAllResults, handleCopyShareUrl, handleQueryFeedback, handleSaveBetSlip, handleSaveQuery, handleSaveRequest, handleShareBetSlip, handleSubmissionComplete, handleUnwatchFixture, handleWatchFixture, hasPropsResults, hasStartedSearch, hasTeamResults, isInitializing, isLiveQuery, isSgpResults, itemsPerPage, lastNotice, loading, navigateTo, noResults, openDeepLink, pageOpacity, pageTranslateY, pendingQuery, playerChipHeadshots, preferredSportsbook, progress, progressText, queryFeedback, queryUsage, requestBroaden, requestedDirection, requestedLine, resolveClarification, responseMetadata, responsive, resultsListForCopyRef, retryFetch, routePayload, searchStatus, searchText, searchedSportsbooks, selectedLegs, selectedOptions, sendToBackground, setBookOverride, setBooksViewOverride, setCurrentPage, setDebugPanelExpanded, setDetectedPromotion, setDidBroaden, setDisableMinBooksFilter, setFilterDeepLinksOnly, setFilterLegCount, setFilterLineType, setFilterMarkets, setFilterPlayers, setFilterPositiveEvOnly, setFilterResultType, setFilterSportsbooks, setPreferredSportsbook, setRelaxMinBooksFromBg, setSearchText, setShowAllMathOverride, setShowBetSlipModal, setShowBookSelector, setShowQueryLimitModal, setShowSavedRequests, setShowShareModal, setSortBy, setSortMethod, sgpGenerationStats, shareUrl, shouldUseNewResultsUI, showAllMathOverride, showBetSlipModal, showBookSelector, showQueryLimitModal, showSavedRequests, showShareModal, showSubmissionScreen, sortBy, sortMethod, toggleMarketFilter, togglePlayerFilter, toggleSportsbookFilter, totalHeaderHeight, undoAutoBroaden, userProfile, watchedFixtures };
}
